import type { RJSFSchema } from '@rjsf/utils';

import { request } from '~/services/common';

/**
 * Fixes regex patterns that have unnecessary escapes inside character classes.
 * When using the Unicode flag (/u), escaping characters that don't need escaping
 * inside character classes [] is invalid and will throw an error.
 *
 * AJV v8 uses Unicode mode by default, which is stricter than previous versions.
 *
 * Characters that DON'T need escaping inside []: . $ % & ( ) * + , @ # ! = { } | /
 * Characters that DO need escaping inside []: \ ^ ] - (at certain positions)
 */
function sanitizeRegexPattern(pattern: string): string {
  let result = '';
  let inCharClass = false;
  let i = 0;

  while (i < pattern.length) {
    const char = pattern[i];

    if (char === '\\' && i + 1 < pattern.length) {
      const nextChar = pattern[i + 1];

      // Check if we're escaping a character class opening bracket
      if (nextChar === '[') {
        result += '\\[';
        i += 2;
        continue;
      }

      // If inside character class, check if the escape is unnecessary
      if (inCharClass) {
        // These characters don't need escaping inside character classes
        const unnecessaryEscapes = [
          '.',
          '$',
          '%',
          '&',
          '(',
          ')',
          '*',
          '+',
          ',',
          '@',
          '#',
          '!',
          '=',
          '{',
          '}',
          '|',
          '/',
        ];
        if (unnecessaryEscapes.includes(nextChar)) {
          // Remove the unnecessary backslash, just add the character
          result += nextChar;
          i += 2;
          continue;
        }
      }

      // Keep the escape sequence as-is
      result += char + nextChar;
      i += 2;
      continue;
    }

    // Track entering/exiting character classes
    if (char === '[' && (i === 0 || pattern[i - 1] !== '\\')) {
      inCharClass = true;
    } else if (char === ']' && inCharClass && (i === 0 || pattern[i - 1] !== '\\')) {
      inCharClass = false;
    }

    result += char;
    i++;
  }

  return result;
}

/**
 * Recursively sanitizes regex patterns in a JSON schema.
 * This fixes patterns that would be invalid with the Unicode flag.
 *
 * @param schema - The JSON schema to sanitize
 * @returns A new schema with sanitized regex patterns
 */
export function sanitizeSchemaPatterns(schema: RJSFSchema): RJSFSchema {
  if (!schema || typeof schema !== 'object') {
    return schema;
  }

  const sanitized: RJSFSchema = { ...schema };

  // Sanitize pattern at current level
  if (typeof sanitized.pattern === 'string') {
    sanitized.pattern = sanitizeRegexPattern(sanitized.pattern);
  }

  // Recursively sanitize API response wrapper properties (patch, put, post, get, delete)
  ['patch', 'put', 'post', 'get', 'delete'].forEach((method) => {
    if (sanitized[method] && typeof sanitized[method] === 'object') {
      sanitized[method] = sanitizeSchemaPatterns(sanitized[method] as RJSFSchema);
    }
  });

  // Recursively sanitize body wrapper (common in API responses like patch.body.schema)
  if (sanitized.body && typeof sanitized.body === 'object') {
    sanitized.body = sanitizeSchemaPatterns(sanitized.body as RJSFSchema);
  }

  // Recursively sanitize schema wrapper (common in API responses like patch.body.schema)
  if (sanitized.schema && typeof sanitized.schema === 'object') {
    sanitized.schema = sanitizeSchemaPatterns(sanitized.schema as RJSFSchema);
  }

  // Recursively sanitize properties
  if (sanitized.properties && typeof sanitized.properties === 'object') {
    sanitized.properties = Object.entries(sanitized.properties).reduce((acc, [key, value]) => {
      acc[key] = sanitizeSchemaPatterns(value as RJSFSchema);
      return acc;
    }, {} as Record<string, RJSFSchema>);
  }

  // Recursively sanitize items (for arrays)
  if (sanitized.items) {
    if (Array.isArray(sanitized.items)) {
      sanitized.items = sanitized.items.map((item) => sanitizeSchemaPatterns(item as RJSFSchema));
    } else if (typeof sanitized.items === 'object') {
      sanitized.items = sanitizeSchemaPatterns(sanitized.items as RJSFSchema);
    }
  }

  // Recursively sanitize allOf, anyOf, oneOf
  ['allOf', 'anyOf', 'oneOf'].forEach((key) => {
    if (Array.isArray(sanitized[key])) {
      sanitized[key] = sanitized[key].map((item: RJSFSchema) => sanitizeSchemaPatterns(item));
    }
  });

  // Recursively sanitize definitions/$defs
  ['definitions', '$defs'].forEach((key) => {
    if (sanitized[key] && typeof sanitized[key] === 'object') {
      sanitized[key] = Object.entries(sanitized[key]).reduce((acc, [defKey, value]) => {
        acc[defKey] = sanitizeSchemaPatterns(value as RJSFSchema);
        return acc;
      }, {} as Record<string, RJSFSchema>);
    }
  });

  // Recursively sanitize additionalProperties
  if (sanitized.additionalProperties && typeof sanitized.additionalProperties === 'object') {
    sanitized.additionalProperties = sanitizeSchemaPatterns(sanitized.additionalProperties as RJSFSchema);
  }

  // Recursively sanitize if/then/else
  ['if', 'then', 'else'].forEach((key) => {
    if (sanitized[key] && typeof sanitized[key] === 'object') {
      sanitized[key] = sanitizeSchemaPatterns(sanitized[key] as RJSFSchema);
    }
  });

  return sanitized;
}

/**
 * Helper to fetch a JSON schema via OPTIONS request and sanitize regex patterns.
 * This fixes patterns that would be invalid with the Unicode flag used by AJV validator.
 *
 * @param url - The URL to fetch the schema from
 * @returns The sanitized schema response
 * TODO: This is a temporary solution to fix the regex patterns in the schema. We need to update backend services to return correct regex patterns.
 */
export async function fetchAndSanitizeSchema(url: string) {
  const response = await request(url, { method: 'OPTIONS' });
  return sanitizeSchemaPatterns(response);
}
